import React, { useEffect, useState } from 'react';
import { Dialog, DialogRoot } from '~/components/ui/Dialog';
import { classNames } from '~/utils/classNames';

export type UploadIntent = 'standard' | 'brochure';

interface IntentOption {
  id: UploadIntent;
  icon: string;
  label: string;
  description: string;
}

const OPTIONS: IntentOption[] = [
  {
    id: 'brochure',
    icon: 'i-ph:magic-wand',
    label: 'Brochure — build my website from this',
    description: "We'll read the PDF's branding, colours and content and design a matching site.",
  },
  {
    id: 'standard',
    icon: 'i-ph:paperclip',
    label: 'Standard attachment',
    description: 'Attach the file as reference material for the conversation.',
  },
];

interface UploadIntentDialogProps {
  isOpen: boolean;
  fileName: string;
  /** Which option starts highlighted. PDFs default to brochure (the dominant intent). */
  defaultIntent?: UploadIntent;
  onClose: () => void;
  onChoose: (intent: UploadIntent) => void;
}

/**
 * Homepage-only intent picker. When a user uploads a PDF on the hero composer we
 * ask whether it's a plain attachment or a brochure to build a site from — the
 * two used to be separate, indistinguishable buttons (DEV-142).
 */
export const UploadIntentDialog: React.FC<UploadIntentDialogProps> = ({
  isOpen,
  fileName,
  defaultIntent = 'brochure',
  onClose,
  onChoose,
}) => {
  const [selected, setSelected] = useState<UploadIntent>(defaultIntent);

  // Reset the highlighted option every time the dialog reopens.
  useEffect(() => {
    if (isOpen) {
      setSelected(defaultIntent);
    }
  }, [isOpen, defaultIntent]);

  return (
    <DialogRoot open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {isOpen && (
        <Dialog showCloseButton onClose={onClose} onBackdrop={onClose} className="max-w-[440px]">
          <div className="p-6">
            <h2 className="font-geist text-[15px] font-medium text-primary">What's this file for?</h2>
            <p className="mt-1 font-geist text-[12.5px] text-muted truncate" title={fileName}>
              {fileName}
            </p>

            <div className="mt-4 flex flex-col gap-2" role="radiogroup" aria-label="Upload intent">
              {OPTIONS.map((option) => {
                const isActive = selected === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    onClick={() => setSelected(option.id)}
                    onDoubleClick={() => onChoose(option.id)}
                    className={classNames(
                      'flex items-start gap-3 text-left px-3.5 py-3 rounded-lg border transition-colors cursor-pointer',
                      isActive
                        ? 'bg-accent/[0.08] border-accent/40'
                        : 'bg-transparent border-hairline hover:border-subtle',
                    )}
                  >
                    <span
                      className={classNames(
                        option.icon,
                        'text-[16px] mt-0.5 flex-shrink-0 transition-colors',
                        isActive ? 'text-accent' : 'text-faint',
                      )}
                    />
                    <span className="flex flex-col gap-0.5 min-w-0">
                      <span className="font-geist text-[13px] font-medium text-primary">{option.label}</span>
                      <span className="font-geist text-[11.5px] text-muted leading-snug">{option.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg font-geist text-[12.5px] border border-hairline text-secondary hover:text-primary hover:border-subtle transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onChoose(selected)}
                className="ui-btn-accent px-4 py-2 rounded-lg font-geist text-[12.5px]"
                autoFocus
              >
                Continue
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </DialogRoot>
  );
};

export default UploadIntentDialog;
